import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

import { activeAdminTenantId, useAuth } from '../../auth/AuthContext';
import { listTenantEvents, type TenantEvent } from '../../api/crashReports';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';

/**
 * Tenant Messages page — ROADMAP #35 part (e), now its own sidebar
 * entry instead of a Dashboard card.
 *
 * Chronological feed of tenant-visible system events. Today the only
 * writers are the crash-report workflow ("Crash log uploaded to
 * 10NEWTONS." / "10NEWTONS started evaluating your crash log."),
 * but the shape is reusable for future suspension warnings,
 * MFA-policy changes, and other tenant-facing platform messages.
 *
 * Read-only; rows are written server-side. Newest-first, capped at
 * the API's default page size (200 hard cap) — pagination + filters
 * can land if volumes grow.
 */

const PAGE_LIMIT = 100;

function formatTs(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} d ago`;
  return formatTs(iso);
}

/**
 * Map event_type → MUI color for the small left-edge marker. Unknown
 * types fall back to neutral grey so a future backend addition
 * renders cleanly without a portal change.
 */
function eventColor(eventType: string): string {
  if (eventType === 'crash_report_uploaded') return 'warning.main';
  if (eventType === 'crash_report_downloaded') return 'info.main';
  return 'text.disabled';
}

export function TenantMessages({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const [events, setEvents] = useState<TenantEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    setRefreshing(true);
    try {
      const page = await listTenantEvents(tenantId, { limit: PAGE_LIMIT });
      setEvents(page.items);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load messages'));
    } finally {
      setRefreshing(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!tenantId) {
    // Defence-in-depth — same warning shape TenantParticipants /
    // TenantSettings use when the user lost their tenant_admin role
    // mid-session.
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. If this
        is unexpected, sign out and back in to refresh your session.
      </Alert>
    );
  }

  const refresh = (
    <RefreshButton onClick={() => void reload()} refreshing={refreshing} />
  );

  return (
    <Stack spacing={3} sx={embedded ? undefined : { maxWidth: 880 }}>
      {actionsSlot ? (
        createPortal(refresh, actionsSlot)
      ) : (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
        >
          {!embedded && (
            <Stack>
              <Typography variant="h4">Messages</Typography>
              <Typography variant="body2" color="text.secondary">
                System messages from 10NEWTONS about your tenant — today
                mostly crash-report acknowledgments. Newest first.
              </Typography>
            </Stack>
          )}
          {refresh}
        </Stack>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {events === null && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {events !== null && events.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <NotificationsNoneIcon
            sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body1" color="text.secondary">
            No messages yet. When 10NEWTONS has something to share —
            a crash-report acknowledgment, a policy change, a
            suspension warning — it will appear here.
          </Typography>
        </Paper>
      )}

      {events !== null && events.length > 0 && (
        <Card>
          <CardContent>
            <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
              {events.map((e) => (
                <Stack
                  key={e.id}
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{ py: 1.25 }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      mt: 0.7,
                      borderRadius: '50%',
                      bgcolor: eventColor(e.event_type),
                      flex: 'none',
                    }}
                  />
                  {/* Message fills the row; the timestamp sits in its own
                      right-aligned column. */}
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                    {e.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: 'none', whiteSpace: 'nowrap', pt: 0.25 }}
                  >
                    {formatRelative(e.created_at)} · {formatTs(e.created_at)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
